import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  ShoppingCart, User, Zap, Menu, X, LayoutDashboard, LogOut,
  Package, Wrench, RefreshCcw, MapPin, ChevronDown, Search, Clock
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";

const navItems = [
  { to: "/shop", label: "Shop", icon: Package },
  { to: "/services", label: "Services", icon: Wrench },
  { to: "/buyback", label: "Sell device", icon: RefreshCcw },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [query, setQuery] = useState("");

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(query.trim() ? `/shop?q=${encodeURIComponent(query.trim())}` : "/shop");
  };

  return (
    <header className="vm-glass sticky top-0 z-50" data-testid="vm-navbar">
      <div className="max-w-7xl mx-auto px-4 lg:px-6 h-16 flex items-center gap-3">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-logo">
          <div className="h-9 w-9 rounded-xl bg-grass-500 flex items-center justify-center shadow-soft">
            <Zap className="h-5 w-5 text-white" strokeWidth={2.6} />
          </div>
          <div className="leading-tight hidden sm:block">
            <p className="font-display text-lg font-bold text-[#0F1115]">
              Quiktronic
            </p>
            <p className="vm-delivery-pill -ml-1 mt-0.5 !py-0 !px-2 !text-[10px]">
              <Clock className="h-3 w-3" /> 30-min delivery
            </p>
          </div>
        </Link>

        <button
          className="hidden md:flex items-center gap-1.5 px-3 py-2 rounded-lg hover:bg-gray-100 text-left max-w-[180px]"
          data-testid="nav-address"
        >
          <MapPin className="h-4 w-4 text-grass-600 shrink-0" />
          <div className="text-xs leading-tight truncate">
            <p className="font-semibold text-[#0F1115]">Deliver to</p>
            <p className="text-gray-500 truncate">Set your address</p>
          </div>
          <ChevronDown className="h-4 w-4 text-gray-400" />
        </button>

        <form onSubmit={submitSearch} className="hidden md:flex flex-1 max-w-xl relative" data-testid="nav-search-form">
          <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            placeholder='Search "iPhone", "AirPods", "TV"…'
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="vm-input pl-9 py-2.5 text-sm bg-[#F4F6F8] border-transparent focus:bg-white"
            data-testid="nav-search-input"
          />
        </form>

        <nav className="hidden lg:flex items-center gap-1 shrink-0">
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `px-3 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-1.5 ${
                  isActive ? "bg-grass-50 text-grass-700" : "text-gray-700 hover:bg-gray-100"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 ml-auto">
          <Link
            to="/cart"
            className="relative h-10 px-4 rounded-lg bg-grass-500 hover:bg-grass-600 text-white font-semibold flex items-center gap-2 text-sm transition-colors"
            data-testid="nav-cart-button"
          >
            <ShoppingCart className="h-4 w-4" />
            <span className="hidden sm:inline">Cart</span>
            {count > 0 && (
              <span className="h-5 min-w-5 px-1 rounded-full bg-white text-grass-700 text-[10px] font-bold flex items-center justify-center">
                {count}
              </span>
            )}
          </Link>

          {user ? (
            <div className="relative">
              <button
                onClick={() => setProfileOpen((v) => !v)}
                className="h-10 w-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors overflow-hidden"
                data-testid="nav-profile-button"
              >
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="h-full w-full object-cover" />
                ) : (
                  <User className="h-5 w-5 text-gray-700" />
                )}
              </button>
              {profileOpen && (
                <div
                  className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-2xl p-2 shadow-soft"
                  data-testid="profile-menu"
                  onMouseLeave={() => setProfileOpen(false)}
                >
                  <div className="px-3 py-3 border-b border-gray-100">
                    <p className="text-sm font-semibold truncate text-[#0F1115]">{user.name}</p>
                    <p className="text-xs text-gray-500 truncate">{user.email}</p>
                    {user.role === "admin" && (
                      <span className="vm-chip vm-chip-primary mt-2">Admin</span>
                    )}
                  </div>
                  <button
                    onClick={() => { setProfileOpen(false); navigate("/profile"); }}
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 text-sm flex items-center gap-2 text-gray-800"
                    data-testid="profile-menu-account"
                  >
                    <User className="h-4 w-4" /> My orders
                  </button>
                  {user.role === "admin" && (
                    <button
                      onClick={() => { setProfileOpen(false); navigate("/admin"); }}
                      className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 text-sm flex items-center gap-2 text-gray-800"
                      data-testid="profile-menu-admin"
                    >
                      <LayoutDashboard className="h-4 w-4" /> Admin panel
                    </button>
                  )}
                  <button
                    onClick={() => { setProfileOpen(false); logout(); }}
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 text-sm flex items-center gap-2 text-[#E11D48]"
                    data-testid="profile-menu-logout"
                  >
                    <LogOut className="h-4 w-4" /> Sign out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              className="vm-btn-primary text-sm hidden sm:inline-flex"
              onClick={() => navigate("/login")}
              data-testid="nav-signin-button"
            >
              Sign in
            </button>
          )}

          <button
            className="lg:hidden h-10 w-10 rounded-lg hover:bg-gray-100 flex items-center justify-center"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile: search bar always visible */}
      <div className="md:hidden border-t border-gray-100 px-4 py-2.5 bg-white">
        <form onSubmit={submitSearch} className="relative">
          <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            placeholder="Search electronics, brands…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="vm-input pl-9 py-2.5 text-sm bg-[#F4F6F8] border-transparent focus:bg-white"
            data-testid="nav-mobile-search"
          />
        </form>
      </div>

      {open && (
        <div className="lg:hidden border-t border-gray-100 bg-white">
          <div className="max-w-7xl mx-auto px-4 py-2 flex flex-col gap-1">
            {navItems.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                onClick={() => setOpen(false)}
                className="px-3 py-3 rounded-lg text-sm font-semibold hover:bg-gray-100 flex items-center gap-3 text-gray-800"
                data-testid={`nav-mobile-${label.toLowerCase().replace(/\s/g, "-")}`}
              >
                <Icon className="h-4 w-4 text-grass-600" /> {label}
              </NavLink>
            ))}
            {!user && (
              <button
                onClick={() => { setOpen(false); navigate("/login"); }}
                className="vm-btn-primary text-sm mt-2"
                data-testid="nav-mobile-signin"
              >
                Sign in with Google
              </button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
